import React from 'react'
import {
  Button,
  Input,
  InputRef,
  Radio,
  Select,
  Space,
  Tag,
  Tooltip,
  TreeSelect,
  Typography
} from 'antd'
import {
  FilterFilled,
  HistoryOutlined,
  InfoCircleOutlined,
  SearchOutlined
} from '@ant-design/icons'
import { MenuInfo } from 'rc-menu/lib/interface'
import { ColumnsType } from 'antd/es/table'
import { StatusDropdown } from 'components/ui/StatusDropdown/StatusDropdown'
import { highlightText } from 'shared/utils/highlightSearchText'
import noProductImage from 'assets/images/no-image-product.png'
import { IMAGES_URL } from 'shared/constants/urls'
import {
  BLOCKED_PRODUCT_HINT,
  getProductStatusItemsWithManualToggle,
  productTags
} from 'shared/constants/product'
import { ERoles } from 'models/auth'
import {
  EProductDisabledReason,
  EProductStatus,
  EProductType,
  IParsedCategory,
  IProductResponse
} from 'models/product'
import { ECategoryType, IGoodsListSellerOption, ISelectedCategory } from '../types'
import { QuickEditCell } from '../ui/QuickEditCell'
import styles from '../GoodsList.module.scss'

const disabledReasonLabels: Record<EProductDisabledReason, string> = {
  [EProductDisabledReason.MANUAL]: 'Отключено вручную',
  [EProductDisabledReason.VARIATION_MANUAL]: 'Вариация отключена вручную',
  [EProductDisabledReason.HS_CATEGORY]: 'Подкатегория скрыта',
  [EProductDisabledReason.MOYSKLAD_GROUP]: 'Скрыто настройкой МойСклад',
  [EProductDisabledReason.MOYSKLAD_SYNC_ERROR]: 'Ошибка синхронизации МойСклад',
  [EProductDisabledReason.SELLER_DISABLED]: 'Отключен продавец',
  [EProductDisabledReason.EXCEL_STOCK_OUTDATED]: 'Устарели Excel-остатки',
  [EProductDisabledReason.STOCK_OUTDATED]: 'Остатки не подтверждены',
  [EProductDisabledReason.DIMENSION_INCOMPLETE]: 'Не заполнены габариты'
}

const getDisabledReasonLabel = (
  reason?: EProductDisabledReason | null
) => reason ? disabledReasonLabels[reason] ?? reason : 'Не указана'

const disabledReasonContent: Record<
  EProductDisabledReason,
  { title: string; action: string }
> = {
  [EProductDisabledReason.MANUAL]: {
    title: 'Товар отключен вручную',
    action: 'Если товар готов к продаже, включите его обратно.'
  },
  [EProductDisabledReason.VARIATION_MANUAL]: {
    title: 'Вариация отключена вручную',
    action: 'Если вариация снова в продаже, включите её обратно. Основной товар и другие вариации это не затрагивает.'
  },
  [EProductDisabledReason.HS_CATEGORY]: {
    title: 'Подкатегория скрыта из выдачи',
    action:
      'Товар вернётся на витрину, когда подкатегорию снова покажут: продавец — в разделе «Профиль» → «Основная информация» → «Скрыть категории», или площадка, если скрыла её для всех.'
  },
  [EProductDisabledReason.MOYSKLAD_GROUP]: {
    title: 'Группа скрыта в МойСклад',
    action: 'Включите отображение группы в настройках синхронизации МойСклад или перенесите товар в отображаемую группу.'
  },
  [EProductDisabledReason.MOYSKLAD_SYNC_ERROR]: {
    title: 'Ошибка синхронизации МойСклад',
    action: 'Проверьте API-токен и запустите синхронизацию повторно. Если остатки актуальны, подтвердите их вручную.'
  },
  [EProductDisabledReason.SELLER_DISABLED]: {
    title: 'Продавец отключен',
    action: 'Обратитесь к менеджеру HorseSmart.'
  },
  [EProductDisabledReason.EXCEL_STOCK_OUTDATED]: {
    title: 'Excel-остатки устарели',
    action: 'Загрузите свежий Excel-файл или подтвердите актуальность остатков.'
  },
  [EProductDisabledReason.STOCK_OUTDATED]: {
    title: 'Остатки не подтверждены',
    action: 'Подтвердите актуальность остатков или обновите их через Excel/МойСклад.'
  },
  [EProductDisabledReason.DIMENSION_INCOMPLETE]: {
    title: 'Не заполнены габариты',
    action: 'Укажите ширину, высоту, длину и вес больше нуля и включите товар. Иначе он вернётся на витрину сам после ночной проверки.'
  }
}

interface IDisabledReasonRecord {
  status?: EProductStatus
  disabled_by?: EProductDisabledReason | null
  disabled_reasons?: EProductDisabledReason[]
}

const getPrimaryDisabledReason = (record: IDisabledReasonRecord) =>
  record.disabled_by ?? record.disabled_reasons?.[0] ?? null

export const renderDisabledReasonInfo = (
  record: IDisabledReasonRecord,
  isSuperAdmin: boolean
) => {
  const reasons = record.disabled_reasons ?? []
  const primaryReason = getPrimaryDisabledReason(record)

  if (record.status !== EProductStatus.DISABLED || !primaryReason) {
    return null
  }

  const content = disabledReasonContent[primaryReason] ?? {
    title: getDisabledReasonLabel(primaryReason),
    action: 'Обратитесь к менеджеру HorseSmart.'
  }
  const additionalReasons = Array.from(
    new Set(reasons.filter((reason) => reason !== primaryReason))
  )

  return (
    <Tooltip
      placement='topLeft'
      color='#ffffff'
      overlayClassName={styles.disabledReasonOverlay}
      title={(
        <div className={styles.disabledReasonTooltip}>
          <div className={styles.disabledReasonTooltipHeader}>
            <InfoCircleOutlined />
            <Typography.Text className={styles.disabledReasonTooltipTitle}>
              Почему товар отключен
            </Typography.Text>
          </div>
          {isSuperAdmin ? (
            <>
              <div className={styles.disabledReasonPrimary}>
                <Typography.Text className={styles.disabledReasonLabel}>
                  Главная причина
                </Typography.Text>
                <Typography.Text className={styles.disabledReasonPrimaryText}>
                  {content.title}
                </Typography.Text>
                <Typography.Text className={styles.disabledReasonAction}>
                  {content.action}
                </Typography.Text>
              </div>
              {additionalReasons.length > 0 && (
                <>
                  <Typography.Text className={styles.disabledReasonLabel}>
                    Дополнительные ограничения
                  </Typography.Text>
                  <div className={styles.disabledReasonTags}>
                    {additionalReasons.map((reason) => (
                      <Tag key={reason} color='blue'>
                        {getDisabledReasonLabel(reason)}
                      </Tag>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <div className={styles.disabledReasonPrimary}>
                <Typography.Text className={styles.disabledReasonPrimaryText}>
                  {content.title}
                </Typography.Text>
              </div>
              <Typography.Text className={styles.disabledReasonAction}>
                Что сделать: {content.action}
              </Typography.Text>
            </>
          )}
        </div>
      )}
    >
      <InfoCircleOutlined className={styles.disabledReasonIcon} />
    </Tooltip>
  )
}

interface GetGoodsListColumnsParams {
  categories: IParsedCategory[]
  normalizedSellers: IGoodsListSellerOption[]
  nameSearch: string
  setNameSearch: React.Dispatch<React.SetStateAction<string>>
  nameSearchRef: React.RefObject<InputRef>
  onSaveSearchName: (value: string) => void
  sellerSearch: string | undefined
  setSellerSearch: React.Dispatch<React.SetStateAction<string | undefined>>
  selectedSellerId: string
  setSelectedSellerId: React.Dispatch<React.SetStateAction<string>>
  onSaveSeller: (sellerId: string) => void
  categorySearch: string | undefined
  setCategorySearch: React.Dispatch<React.SetStateAction<string | undefined>>
  selectedCategory: ISelectedCategory | null
  setSelectedCategory: React.Dispatch<React.SetStateAction<ISelectedCategory | null>>
  onSaveCategory: (selectedCategory: ISelectedCategory | null) => void
  status: EProductStatus | undefined
  setStatus: React.Dispatch<React.SetStateAction<EProductStatus | undefined>>
  onChangeStatus: (status: EProductStatus | undefined) => void
  role: ERoles | null | undefined
  isSuperAdmin: boolean
  sellerId?: string
  openProductInNewTab: (productId: string) => void
  onTagClick: (event: MenuInfo, itemId: string) => void
  draftPrices: Record<string, string>
  draftStocks: Record<string, string>
  setDraftPrices: React.Dispatch<React.SetStateAction<Record<string, string>>>
  setDraftStocks: React.Dispatch<React.SetStateAction<Record<string, string>>>
  handlePriceBlur: (record: IProductResponse) => void
  handleStockBlur: (record: IProductResponse) => void
  isFieldSaving: (productId: string, field: 'price' | 'in_stock') => boolean
  onOpenPriceHistory: (productId: string) => void
  articleMode: 'article' | 'vendor_code'
  globalSearch: string
}

export const getGoodsListColumns = ({
  categories,
  normalizedSellers,
  nameSearch,
  setNameSearch,
  nameSearchRef,
  onSaveSearchName,
  sellerSearch,
  setSellerSearch,
  selectedSellerId,
  setSelectedSellerId,
  onSaveSeller,
  categorySearch,
  setCategorySearch,
  selectedCategory,
  setSelectedCategory,
  onSaveCategory,
  status,
  setStatus,
  onChangeStatus,
  role,
  isSuperAdmin,
  sellerId,
  openProductInNewTab,
  onTagClick,
  draftPrices,
  draftStocks,
  setDraftPrices,
  setDraftStocks,
  handlePriceBlur,
  handleStockBlur,
  isFieldSaving,
  onOpenPriceHistory,
  articleMode,
  globalSearch
}: GetGoodsListColumnsParams): ColumnsType<IProductResponse> => {
  const baseColumns: ColumnsType<IProductResponse> = [
    {
      title: 'Артикул',
      dataIndex: articleMode,
      width: isSuperAdmin && !sellerId ? '9%' : '12%',
      onCell: ({ _id }) => ({ onClick: () => openProductInNewTab(_id) }),
      render: (_, record) => (
        <Typography.Text style={{ wordWrap: 'break-word' }}>
          {highlightText(
            (articleMode === 'article' ? record.article : record.vendor_code) ?? '',
            globalSearch
          )}
        </Typography.Text>
      )
    },
    {
      title: 'Фото',
      dataIndex: 'photo',
      width: 95,
      onCell: ({ _id }) => ({ onClick: () => openProductInNewTab(_id) }),
      render: (_, { photo }) =>
        photo && photo.length ? (
          <div
            style={{
              width: 62,
              height: 56,
              backgroundImage: `url(${IMAGES_URL}${photo[0]})`,
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          />
        ) : (
          <img
            src={noProductImage}
            alt='Изображение товара'
            width={62}
            height={56}
          />
        )
    },
    {
      title: 'Наименование товара',
      dataIndex: 'name',
      width: sellerId ? '24%' : isSuperAdmin ? '24%' : '34%',
      onCell: ({ _id }) => ({ onClick: () => openProductInNewTab(_id) }),
      render: (_, { name }) => <Typography.Text>{highlightText(name, nameSearch || globalSearch)}</Typography.Text>,
      filterDropdown: ({ close }) => (
        <div className='filterWrapper' onKeyDown={(e) => e.stopPropagation()}>
          <Input
            ref={nameSearchRef}
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
            onPressEnter={() => {
              onSaveSearchName(nameSearch)
              close()
            }}
            placeholder='Наименование товара'
            className='filterInput'
          />
          <div className='filterFooterButtons'>
            <Button
              type='primary'
              onClick={() => {
                onSaveSearchName(nameSearch)
                close()
              }}
              icon={<SearchOutlined />}
              size='small'
              className='filterButton'
            >
              Найти
            </Button>
            <Button
              onClick={() => {
                setNameSearch('')
                onSaveSearchName('')
                close()
              }}
              size='small'
              className='filterButton'
            >
              Очистить
            </Button>
            <Button type='link' size='small' onClick={() => close()}>
              Закрыть
            </Button>
          </div>
        </div>
      ),
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => nameSearchRef.current?.focus(), 0)
        }
      },
      filterIcon: () => (
        <SearchOutlined
          style={{
            color: nameSearch ? '#1677ff' : 'rgba(0, 0, 0, 0.29)'
          }}
        />
      )
    },
    {
      title: 'Категория',
      dataIndex: 'category_id',
      width: sellerId ? '20%' : isSuperAdmin ? '17%' : '23%',
      onCell: ({ _id }) => ({ onClick: () => openProductInNewTab(_id) }),
      render: (_, { category_id, subCategory_id }) => {
        if (!category_id || !subCategory_id) {
          return <Typography.Text style={{ opacity: 0.5 }}>Не задана</Typography.Text>
        }

        return (
          <>
            <Typography.Text>{category_id.name + ' >'}</Typography.Text>
            <br />
            <Typography.Text>{subCategory_id.name}</Typography.Text>
          </>
        )
      },
      filterDropdown: ({ close }) => (
        <div className='filterWrapper' onKeyDown={(e) => e.stopPropagation()}>
          <TreeSelect
            value={categorySearch}
            onChange={(value) => {
              setCategorySearch(value)
              setSelectedCategory(null)
            }}
            className='filterInput filterTreeSelect'
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            placeholder='Выберите категорию или подкатегорию'
            onSelect={(_, item) => {
              setSelectedCategory({
                id: item.value,
                type: item.type as ECategoryType
              })
            }}
            treeData={categories}
            showSearch
            treeNodeFilterProp='title'
            allowClear
          />
          <div className='filterFooterButtons'>
            <Button
              type='primary'
              onClick={() => {
                onSaveCategory(selectedCategory)
                close()
              }}
              icon={<SearchOutlined />}
              size='small'
              className='filterButton'
            >
              Найти
            </Button>
            <Button
              onClick={() => {
                setCategorySearch(undefined)
                onSaveCategory(null)
                close()
              }}
              size='small'
              className='filterButton'
            >
              Очистить
            </Button>
            <Button type='link' size='small' onClick={() => close()}>
              Закрыть
            </Button>
          </div>
        </div>
      ),
      filterIcon: () => (
        <FilterFilled
          style={{
            color: selectedCategory ? '#1677ff' : 'rgba(0, 0, 0, 0.25)'
          }}
        />
      )
    },
    {
      title: 'Остаток',
      dataIndex: 'dimension',
      width: '14%',
      render: (_, record) => (
        <QuickEditCell
          value={draftStocks[record._id] ?? String(record.dimension?.in_stock ?? '')}
          onChange={(value) =>
            setDraftStocks((prev) => ({
              ...prev,
              [record._id]: value
            }))
          }
          onBlur={() => handleStockBlur(record)}
          disabled={isFieldSaving(record._id, 'in_stock')}
          allowPattern={/^\d*$/}
          className={styles.quickEditInput}
        />
      )
    },
    {
      title: 'Цена',
      dataIndex: 'price',
      width: '15%',
      render: (_, record) => (
        <div className={styles.priceCell}>
          <QuickEditCell
            value={
              draftPrices[record._id] ??
              (record.price ? Number(record.price).toLocaleString('ru-RU') : '')
            }
            onChange={(value) =>
              setDraftPrices((prev) => ({
                ...prev,
                [record._id]: value
              }))
            }
            onBlur={() => handlePriceBlur(record)}
            disabled={isFieldSaving(record._id, 'price')}
            allowPattern={/^[\d\s,.-]*$/}
            suffix='₽'
            className={`${styles.quickEditInput} ${styles.priceEditInput}`}
          />
          <Tooltip title='История изменений стоимости'>
            <Button
              type='text'
              size='small'
              icon={<HistoryOutlined />}
              aria-label='История изменений стоимости'
              onClick={() => onOpenPriceHistory(record._id)}
            />
          </Tooltip>
        </div>
      )
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      width: '16%',
      onCell: ({ _id }) => ({ onClick: () => openProductInNewTab(_id) }),
      render: (_, record) => {
        const { status, _id } = record
        const statusItems = getProductStatusItemsWithManualToggle(
          status,
          role as ERoles
        )

        const isVariation = record.type === EProductType.VARIATION
        const productTypeLabel = isVariation ? 'Вариация' : 'Основной товар'

        // aggregate() не применяет Mongoose defaults — старые документы могут не иметь поля
        const ratingScore = record.ratingScore ?? 0
        const ratingColor =
          ratingScore >= 80 ? '#52c41a' :
          ratingScore >= 60 ? '#faad14' :
          ratingScore >= 40 ? '#fa8c16' :
          '#ff4d4f'

        const subInfo = (
          <>
            <Typography.Text style={{ fontSize: 11, display: 'block', marginTop: 3, color: 'rgba(0, 0, 0, 0.88)' }}>
              {productTypeLabel}
            </Typography.Text>
            {/* Рейтинг заполненности считается только для основных товаров */}
            {!isVariation && (
              <Typography.Text style={{ fontSize: 11, display: 'block', color: ratingColor }}>
                Рейтинг: {ratingScore}
              </Typography.Text>
            )}
          </>
        )

        if (!statusItems.length) {
          const statusTag = (
            <Tag color={productTags[status].color}>
              {productTags[status].label}
            </Tag>
          )

          return (
            <div onClick={(event) => event.stopPropagation()}>
              <div className={styles.statusWithReason}>
                {/* продавцу тег «Заблокирован» недоступен для клика — только подсказка почему */}
                {status === EProductStatus.BLOCKED ? (
                  <Tooltip title={BLOCKED_PRODUCT_HINT}>{statusTag}</Tooltip>
                ) : (
                  statusTag
                )}
                {renderDisabledReasonInfo(record, isSuperAdmin)}
              </div>
              {subInfo}
            </div>
          )
        }

        return (
          <div onClick={(event) => event.stopPropagation()}>
            <div className={styles.statusWithReason}>
              <StatusDropdown
                items={statusItems}
                label={productTags[status].label}
                color={productTags[status].color}
                onClick={(event) => onTagClick(event, _id)}
              />
              {renderDisabledReasonInfo(record, isSuperAdmin)}
            </div>
            {subInfo}
          </div>
        )
      },
      filterDropdown: ({ close }) => (
        <div className='filterWrapper' onKeyDown={(e) => e.stopPropagation()}>
          <Radio.Group
            onChange={(e) => {
              setStatus(e.target.value)
              onChangeStatus(e.target.value)
              close()
            }}
            value={status}
            style={{ display: 'block', marginBottom: 15 }}
          >
            <Space direction='vertical' size={15}>
              {Object.values(productTags).map((item) => (
                <Radio key={item.value as string} value={item.value}>
                  <Tag color={item.color}>{item.label}</Tag>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
          <Button
            onClick={() => {
              setStatus(undefined)
              onChangeStatus(undefined)
              close()
            }}
            size='small'
            className='filterButton'
          >
            Очистить
          </Button>
          <Button type='link' size='small' onClick={() => close()}>
            Закрыть
          </Button>
        </div>
      ),
      filterIcon: () => (
        <FilterFilled
          style={{
            color: status ? '#1677ff' : 'rgba(0, 0, 0, 0.25)'
          }}
        />
      )
    }
  ]

  if (isSuperAdmin && !sellerId) {
    const [numberColumn, photoColumn, nameColumn, ...otherColumns] = baseColumns

    const sellerColumn: ColumnsType<IProductResponse>[0] = {
      title: 'Продавец',
      dataIndex: 'shop_name',
      width: '19%',
      onCell: ({ _id }) => ({ onClick: () => openProductInNewTab(_id) }),
      render: (_, { seller_id }) =>
        seller_id?._id && seller_id.shop_details?.shop_name ? (
          <Typography.Text>{seller_id.shop_details.shop_name}</Typography.Text>
        ) : (
          <Typography.Text style={{ opacity: 0.5 }}>
            Удаленный аккаунт
          </Typography.Text>
        ),
      filterDropdown: ({ close }) => (
        <div className='filterWrapper' onKeyDown={(e) => e.stopPropagation()}>
          <Select
            value={sellerSearch}
            onChange={(value) => {
              setSellerSearch(value)
              setSelectedSellerId('')
            }}
            className='filterInput filterSelect'
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            placeholder='Выберите продавца'
            onSelect={(_, item) => setSelectedSellerId(item.key)}
            options={normalizedSellers}
            showSearch
            allowClear
          />
          <div className='filterFooterButtons'>
            <Button
              type='primary'
              onClick={() => {
                onSaveSeller(selectedSellerId)
                close()
              }}
              icon={<SearchOutlined />}
              size='small'
              className='filterButton'
            >
              Найти
            </Button>
            <Button
              onClick={() => {
                setSellerSearch(undefined)
                setSelectedSellerId('')
                onSaveSeller('')
                close()
              }}
              size='small'
              className='filterButton'
            >
              Очистить
            </Button>
            <Button type='link' size='small' onClick={() => close()}>
              Закрыть
            </Button>
          </div>
        </div>
      ),
      filterIcon: () => (
        <FilterFilled
          style={{
            color: selectedSellerId ? '#1677ff' : 'rgba(0, 0, 0, 0.25)'
          }}
        />
      )
    }

    return [numberColumn, photoColumn, nameColumn, sellerColumn, ...otherColumns]
  }

  return baseColumns
}
